import { useState } from "react";
import { useCreate, useModalReturnType, useList } from "@pankod/refine-core";
import {
  Button,
  Form,
  Icons,
  Input,
  Modal,
  Progress,
  Select,
  Upload,
  UploadFile,
} from "@pankod/refine-antd";
import axios from "axios";
import convertMutateData from "components/courses/uploads/utils";
import { ITopicShow, IContent, IVideo, IVideoCreate, IInstructor } from "interfaces";

interface Props {
  modal: useModalReturnType;
  parent: any;
  parentKind: string;
  type: string;
  onUpdate: () => void;
}

const UploadVideoModal: React.FC<Props> = ({ modal, parent, parentKind, type, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [file, setFile] = useState<UploadFile | undefined>(undefined);
  const [form] = Form.useForm();
  const { mutate: videoMutate } = useCreate<IVideoCreate>();
  const { mutate: contentMutate } = useCreate<IContent>();
  const { mutate: optionMutate } = useCreate<IVideo>();
  const { data: instructorsData } = useList<IInstructor>({
    resource: "instructors",
    config: { pagination: { pageSize: 100 } }
  });

  const instructors = instructorsData?.data || [];

  const onClose = () => {
    form.resetFields();
    setFile(undefined);
    setUploading(false);
    setUploadPercentage(0);
    setLoading(false);
    modal.close();
  }

  const onSubmit = async () => {
    setLoading(true);

    const { name, options, instructor_ids } = await form.validateFields();

    try {
      const video = await createVideo({ name, instructor_ids });
      await uploadVideo({ video });
      setUploading(false);
      await Promise.all(
        options.map((option: string) => createOption({ video, option }))
      );
      await createContent({ name, video });

      onUpdate();
      onClose();
    } catch {

    } finally {
      setUploading(false);
      setUploadPercentage(0);
      setLoading(false);
    }
  }

  const createVideo = ({ name, instructor_ids }: { name: string, instructor_ids: string[] }) => (
    new Promise<IVideoCreate>((resolve, reject) => {
      videoMutate({
        resource: "videos",
        values: { name, instructor_ids }
      }, {
        onSuccess: ({ data }) => resolve(data),
        onError: (error) => reject(error),
      });
    })
  );

  const uploadVideo = ({ video }: { video: IVideoCreate }) => {
    const { url } = video.upload;

    setUploading(true);

    return axios.put(url, file, {
      onUploadProgress: ({ loaded, total }) => setUploadPercentage(Math.round(100 * loaded / total))
    })
   }

   const createOption = ({ video, option }: { video: IVideoCreate, option: string }) => (
     new Promise<IVideo>((resolve, reject) => {
       optionMutate({
         resource: "video_options",
         values: {
           provider: option,
           video_id: video.id,
         }
       }, {
         onSuccess: ({ data }) => resolve(data),
         onError: (error) => reject(error),
       })
     })
   );

  const createContent = ({ name, video }: { name: string, video: IVideoCreate }) => {
    const items = parent[type] || [];

    const data = {
      name,
      kind: 'video',
      reference: video.id,
      order: items.length + 1,
      parent_type: parentKind,
      parent_id: parent.id
    }

    const mutateObject = convertMutateData(type, data);

    return new Promise<IContent>((resolve, reject) => {
      contentMutate(mutateObject, {
        onSuccess: ({ data }) => resolve(data),
        onError: (error) => reject(error),
      })
    })
  }

  return (
    <Modal
      title="Subir conteúdo em vídeo"
      visible={modal.visible}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={onSubmit}
          disabled={!file}
        >
          Upload
        </Button>
      ]}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Nome"
          name="name"
          required
        >
          <Input />
        </Form.Item>

        <Form.Item label="Upload" name="upload" required>
          <Upload
            maxCount={1}
            accept="video/mp4"
            fileList={file ? [file] : []}
            onRemove={() => setFile(undefined)}
            beforeUpload={(newFile) => {
              setFile(newFile);

              return false;
            }}
          >
            <Button className="!flex items-center" disabled={loading}>
              <Icons.UploadOutlined /> Vídeo (mp4)
            </Button>
          </Upload>
          {uploading && <Progress percent={uploadPercentage} />}
        </Form.Item>

        <Form.Item
          label="Opções"
          name="options"
          required
        >
          <Select
            mode="multiple"
          >
            <Select.Option value="cloudflare">Cloudflare</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Instrutores"
          name="instructor_ids"
          required
        >
          <Select
            mode="multiple"
          >
            {instructors.map(({ id, name }) => (
              <Select.Option key={id} value={id}>{name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  )
};

export default UploadVideoModal;
